import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { FormInput, Notice, OnChangePseudoEvent } from '@calm-web/design-system';

import BackButton from '@/components/dtcPartnerships/BackButton';
import { BlueLine } from '@/components/dtcPartnerships/Welcome/styles';
import { useAnalytics } from '@/hooks/analytics';
import { useAuthFormInputs } from '@/hooks/forms/useAuthFormInputs';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import genericMessages from '@/messages/messages';

import { SignupScreenProps } from '../shared/types';
import messages from './messages';
import { CtaText, EligibilityContentWrapper, Legal, SubmitButton } from './styles';
import { EligibilityErrors, isEligibilityErrorKey } from './types';

const Eligibility = ({ onScreenChange, partnerDetails, redeem, error, isSubmitting }: SignupScreenProps) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const [inputFields, inputActions] = useAuthFormInputs(['uniqueId'] as const);
	const [showError, setShowError] = useState(false);
	const { user_id_descriptor, pageInfo } = partnerDetails;

	const { onChange, onBlur, onFocus } = inputActions;

	useComponentDidMount(() => {
		logEvent({
			eventName: 'D2C Redemption : Offer Code : Landed',
		});
	});

	useEffect(() => {
		if (isSubmitting) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setShowError(false);
		} else {
			setShowError(true);
		}
	}, [isSubmitting]);

	useEffect(() => {
		if (error && error.code !== 'payment_required') {
			logEvent({
				eventName: 'D2C Signup : Redemption : Error',
				eventProps: {
					partner_id: partnerDetails.id as string,
					integration_type: partnerDetails.integration_type as string,
					...(user_id_descriptor && { user_id_descriptor }),
					error_code: error.code,
				},
			});
		}
	}, [error, logEvent, partnerDetails, user_id_descriptor]);

	async function submitEligibility(e: SyntheticEvent): Promise<void> {
		e.preventDefault();
		e.stopPropagation();
		await redeem(partnerDetails, { partner_user_id: inputFields.uniqueId?.value });
	}

	function handleInputChange(e: OnChangePseudoEvent): void {
		setShowError(false);
		onChange(e);
	}

	function renderError(): React.ReactNode {
		if (
			inputFields.uniqueId.value &&
			showError &&
			error?.code &&
			isEligibilityErrorKey(error.code) &&
			EligibilityErrors[error.code]
		) {
			return (
				<Notice describedBy="submit-eligibility-form">
					{formatMessage(EligibilityErrors[error.code], {
						partnerName: partnerDetails.name,
						supportLink: (...chunks: ReactNode[]) => (
							<a href="https://www.calm.com/contact" target="_blank" rel="noopener noreferrer">
								{chunks}
							</a>
						),
					})}
				</Notice>
			);
		}
		return null;
	}

	return (
		<EligibilityContentWrapper>
			<BackButton onScreenChange={onScreenChange} />
			<CtaText>{formatMessage(messages.offercodeCTA)}</CtaText>
			<BlueLine />
			<form onSubmit={submitEligibility} id="submit-eligibility-form">
				<FormInput
					name="uniqueId"
					label={formatMessage(messages.offercode)}
					onChange={handleInputChange}
					onBlur={e => {
						// We feel confident casting to HTMLInputElement here because that's what's under the hood
						const inputEvent = e as React.FocusEvent<HTMLInputElement>;
						onBlur(inputEvent, { value: inputEvent.currentTarget.value });
					}}
					onFocus={onFocus}
					value={inputFields.uniqueId?.value}
					isValid={Boolean(inputFields.uniqueId?.value)}
					error={
						error?.code === 'b2b_partner_eligibility_redeem_invalid_id' ||
						error?.code === 'b2b_partner_eligibility_redeem_duplicate_id'
							? formatMessage(messages.errorInvalidOfferCode)
							: undefined
					}
				/>
				{pageInfo.messages?.legal ? <Legal>{formatMessage(pageInfo.messages.legal)}</Legal> : null}
				{renderError()}
				<SubmitButton
					backgroundColor="accessibleBrandGradient"
					type="submit"
					isDisabled={!inputFields.uniqueId?.value || isSubmitting}
					legacyOnClick={submitEligibility}
					isLoading={isSubmitting}
					data-testid="b2b-partneruserid-submit"
				>
					{formatMessage(genericMessages.submit)}
				</SubmitButton>
			</form>
		</EligibilityContentWrapper>
	);
};

export default Eligibility;
